/**
 * CP Portal — Rep welcome email settings for operational channel partners.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Mail, RefreshCw } from 'lucide-react';
import { useCpPortal } from '../../hooks/useCpPortal';
import { LetterStyleConfigButton } from '../welcome-email/LetterStyleConfigButton';

const SECTION_LABELS: Record<string, string> = {
  openingHtml: 'Opening',
  partnershipHtml: 'Partnership / compliance',
  loginHtml: 'Wallet login',
  phoneAlignmentHtml: 'Phone alignment',
  bulletsHtml: 'Wallet features (bullets)',
  supportMmHtml: 'MyMoolah support',
  supportPartnerHtml: 'Partner support',
  closingHtml: 'Closing',
};

const PLACEHOLDERS = ['{{firstName}}', '{{displayName}}', '{{walletUrl}}', '{{partnerSupportEmail}}', '{{mmSupportEmail}}'];

const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-700',
  pending_approval: 'bg-amber-50 text-amber-700',
  approved: 'bg-emerald-50 text-emerald-700',
  disabled: 'bg-red-50 text-red-700',
};

interface RepWelcomeConfig {
  status: string | null;
  subject: string;
  partnerSupportEmail: string;
  template: Record<string, string>;
  rejectionNote?: string | null;
  hasLogo: boolean;
  letterFontFamily?: string;
  letterFontSize?: string;
  letterFontOptions?: string[];
  letterFontSizes?: string[];
}

export const ClientWelcomeEmailOverlay: React.FC = () => {
  const { apiBase, authHeaders, cpPath, user, isMirror } = useCpPortal();
  const [config, setConfig] = useState<RepWelcomeConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [logoBlobUrl, setLogoBlobUrl] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [testEmail, setTestEmail] = useState(user?.email || '');

  const headers = useCallback((): Record<string, string> => authHeaders(), [authHeaders]);

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const res = await fetch(`${apiBase}/rep-welcome-email/preview?firstName=Alex`, {
        headers: headers(),
      });
      setPreviewHtml(await res.text());
    } catch {
      setPreviewHtml('<p>Preview unavailable</p>');
    } finally {
      setPreviewLoading(false);
    }
  }, [apiBase, headers]);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/rep-welcome-email`, { headers: headers() });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Load failed');
      setConfig(data.data);
      if (data.data?.partnerSupportEmail) setTestEmail(data.data.partnerSupportEmail);
    } catch (err: unknown) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to load' });
    } finally {
      setLoading(false);
    }
  }, [apiBase, headers]);

  useEffect(() => {
    if (!loading && config) loadPreview();
  }, [loading, config, previewKey, loadPreview]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/rep-welcome-email/logo`, {
          headers: headers(),
        });
        if (!res.ok) return;
        const blob = await res.blob();
        if (!blob.type.startsWith('image/')) return;
        objectUrl = URL.createObjectURL(blob);
        if (!revoked) setLogoBlobUrl(objectUrl);
      } catch {
        /* non-blocking */
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [apiBase, headers]);

  const apiPost = async (path: string, body?: object) => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${apiBase}/rep-welcome-email/${path}`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Request failed');
      setConfig(data.data);
      setPreviewKey((k) => k + 1);
      setMessage({ type: 'success', text: 'Updated successfully' });
    } catch (err: unknown) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Request failed' });
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = async () => {
    if (!config) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${apiBase}/rep-welcome-email`, {
        method: 'PUT',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: config.subject,
          partnerSupportEmail: config.partnerSupportEmail,
          template: config.template,
          letterFontFamily: config.letterFontFamily,
          letterFontSize: config.letterFontSize,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Save failed');
      setConfig(data.data);
      setPreviewKey((k) => k + 1);
      setMessage({ type: 'success', text: 'Draft saved' });
    } catch (err: unknown) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (key: string, value: string) => {
    if (!config) return;
    setConfig({ ...config, template: { ...config.template, [key]: value } });
  };

  if (user && !['maker', 'admin'].includes(user.role || '')) {
    return <Navigate to={cpPath('/dashboard')} replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" />
        Loading...
      </div>
    );
  }

  if (!config) {
    return <div className="p-8 text-center text-gray-500">Welcome email settings unavailable.</div>;
  }

  const isOn = config.status === 'approved';
  const canEdit = config.status !== 'pending_approval' || true;

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3">
        <Mail className="w-6 h-6 text-[var(--primary)]" />
        <div>
          <h1 className="text-2xl font-semibold text-[var(--foreground)]">Rep welcome email</h1>
          <p className="text-sm text-[var(--muted-foreground)]">
            Email sent to reps after they register their MyMoolah wallet via your partner link
          </p>
        </div>
        <span className={`ml-auto text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${STATUS_BADGE[config.status || 'draft']}`}>
          {config.status?.replace(/_/g, ' ') || 'not configured'}
        </span>
      </div>

      {message && (
        <div className={`p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
          {message.text}
        </div>
      )}

      {config.rejectionNote && (
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-100 text-sm text-amber-900">
          <strong>Admin note:</strong> {config.rejectionNote}
        </div>
      )}

      <div className="mymoolah-card p-5 space-y-4">
        <div className="flex items-center gap-4">
          {logoBlobUrl ? (
            <img src={logoBlobUrl} alt="Partner logo" className="w-14 h-14 object-contain rounded border bg-white" />
          ) : (
            <div className="w-14 h-14 rounded border bg-gray-50 flex items-center justify-center text-xs text-gray-400">Logo</div>
          )}
          <div className="text-sm">
            <p className="font-medium">Partner logo</p>
            <Link to="/client/onboarding" className="text-[var(--primary)] text-xs hover:underline">
              Upload logo in KYB onboarding
            </Link>
            {!config.hasLogo && <p className="text-amber-700 text-xs mt-1">No logo uploaded yet</p>}
          </div>
          {config.status === 'approved' && (
            <button type="button" disabled={saving} onClick={() => apiPost('disable')} className="ml-auto text-sm text-red-600 border border-red-200 px-3 py-1.5 rounded-lg">
              Turn OFF
            </button>
          )}
          {config.status === 'disabled' && (
            <button type="button" disabled={saving} onClick={() => apiPost('enable')} className="ml-auto text-sm text-emerald-700 border border-emerald-200 px-3 py-1.5 rounded-lg">
              Turn ON
            </button>
          )}
          {isOn && <span className="ml-auto text-xs text-emerald-700 font-semibold">Sending enabled</span>}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Subject</label>
              <input
                className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                value={config.subject}
                disabled={!canEdit}
                onChange={(e) => setConfig({ ...config, subject: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Partner support email</label>
              <input
                type="email"
                className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                value={config.partnerSupportEmail}
                onChange={(e) => setConfig({ ...config, partnerSupportEmail: e.target.value })}
              />
            </div>

            <div className="flex flex-wrap gap-1">
              {PLACEHOLDERS.map((ph) => (
                <span key={ph} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-mono">{ph}</span>
              ))}
            </div>

            {Object.entries(SECTION_LABELS).map(([key, label]) => (
              <div key={key}>
                <label className="text-xs font-semibold text-gray-500 uppercase">{label}</label>
                <textarea
                  rows={3}
                  className="w-full mt-1 border rounded-lg px-3 py-2 text-sm font-mono"
                  value={config.template[key] || ''}
                  onChange={(e) => updateField(key, e.target.value)}
                />
              </div>
            ))}

            <div className="flex flex-wrap gap-2 pt-2">
              <button type="button" disabled={saving} onClick={saveDraft} className="px-4 py-2 text-sm font-semibold rounded-lg bg-[var(--primary)] text-white">
                Save draft
              </button>
              <button type="button" disabled={saving} onClick={() => apiPost('submit')} className="px-4 py-2 text-sm font-semibold rounded-lg border">
                Submit for approval
              </button>
              {isMirror && config.status === 'pending_approval' && (
                <>
                  <button type="button" disabled={saving} onClick={() => apiPost('approve')} className="px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-600 text-white">
                    Approve
                  </button>
                  <button type="button" disabled={saving} onClick={() => apiPost('reject', { note: 'Rejected from admin mirror' })} className="px-4 py-2 text-sm font-semibold rounded-lg border border-red-200 text-red-700">
                    Reject
                  </button>
                </>
              )}
              <button
                type="button"
                disabled={saving}
                onClick={() => apiPost('test-send', { to: testEmail || user?.email, firstName: 'Alex' })}
                className="px-4 py-2 text-sm font-semibold rounded-lg border"
              >
                Test send
              </button>
              <LetterStyleConfigButton
                fontFamily={config.letterFontFamily || 'calibri'}
                fontSize={config.letterFontSize || '11pt'}
                fontOptions={config.letterFontOptions}
                sizeOptions={config.letterFontSizes}
                disabled={saving}
                onApply={async (letterFontFamily, letterFontSize) => {
                  setConfig({ ...config, letterFontFamily, letterFontSize });
                  setSaving(true);
                  setMessage(null);
                  try {
                    const res = await fetch(`${apiBase}/rep-welcome-email`, {
                      method: 'PUT',
                      headers: { ...headers(), 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        subject: config.subject,
                        partnerSupportEmail: config.partnerSupportEmail,
                        template: config.template,
                        letterFontFamily,
                        letterFontSize,
                      }),
                    });
                    const data = await res.json();
                    if (!data.success) throw new Error(data.error || 'Save failed');
                    setConfig(data.data);
                    setPreviewKey((k) => k + 1);
                    setMessage({ type: 'success', text: 'Letter style saved' });
                  } catch (err: unknown) {
                    setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Save failed' });
                  } finally {
                    setSaving(false);
                  }
                }}
              />
            </div>
            <p className="text-xs text-gray-400">Test send: your contact email or @mymoolah.africa only</p>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Preview</p>
            {previewLoading ? (
              <div className="min-h-[480px] flex items-center justify-center border rounded-lg text-gray-400">Loading preview...</div>
            ) : (
              <iframe
                title="Welcome email preview"
                srcDoc={previewHtml}
                className="w-full min-h-[480px] border rounded-lg bg-white"
                sandbox=""
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientWelcomeEmailOverlay;
